import { useEffect, useMemo, useState } from 'react'
import { Download, Loader2, Lock, LogOut } from 'lucide-react'

import { Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { useTranslations } from '@/hooks/useTranslations'
import {
  clearPortalSession,
  defaultTemplateForBillType,
  exportPortalZip,
  fetchExportPortalBills,
  fetchExportPortalCustomers,
  getPortalToken,
  templatesForBillType,
  verifyExportPortalPin,
  type BillExportTemplate,
  type ExportPortalBillSummary,
  type ExportPortalCustomerOption,
  type ExportPortalFilters,
  type PdfMode,
} from '@/modules/export-portal/services/exportPortal'

const MONTH_KEYS = [
  'month_1',
  'month_2',
  'month_3',
  'month_4',
  'month_5',
  'month_6',
  'month_7',
  'month_8',
  'month_9',
  'month_10',
  'month_11',
  'month_12',
] as const

const ALL_CUSTOMERS = 'all'

function buildYearOptions(): number[] {
  const currentYear = new Date().getFullYear()
  const years: number[] = []
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y)
  return years
}

function formatAmount(n: number) {
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const TEMPLATE_LABEL_KEYS: Record<
  'buy_monthly' | 'sell_all_monthly' | 'sell_retail_monthly' | 'sell_wholesale_all_monthly',
  string
> = {
  buy_monthly: 'template_buy_monthly',
  sell_all_monthly: 'template_sell_all_monthly',
  sell_retail_monthly: 'template_sell_retail_monthly',
  sell_wholesale_all_monthly: 'template_sell_wholesale',
}


export function ExportPortalPage() {
  const t = useTranslations('export_portal')
  const [unlocked, setUnlocked] = useState(() => Boolean(getPortalToken()))
  const [pin, setPin] = useState('')
  const [pinError, setPinError] = useState('')
  const [verifying, setVerifying] = useState(false)

  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [billType, setBillType] = useState<'buy' | 'sell'>('sell')
  const [location, setLocation] = useState<'chanthaburi' | 'chumphon'>('chanthaburi')
  const [customerId, setCustomerId] = useState(ALL_CUSTOMERS)
  const [customers, setCustomers] = useState<ExportPortalCustomerOption[]>([])
  const [loadingCustomers, setLoadingCustomers] = useState(false)

  const [rows, setRows] = useState<ExportPortalBillSummary[] | null>(null)
  const [total, setTotal] = useState(0)
  const [loadingList, setLoadingList] = useState(false)
  const [listError, setListError] = useState('')

  const [template, setTemplate] = useState<BillExportTemplate>(defaultTemplateForBillType('sell'))
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const [exportOk, setExportOk] = useState('')

  const yearOptions = useMemo(() => buildYearOptions(), [])
  const availableTemplates = templatesForBillType(billType)

  useEffect(() => {
    if (!availableTemplates.includes(template)) {
      setTemplate(defaultTemplateForBillType(billType))
    }
  }, [availableTemplates, billType, template])

  const filters: ExportPortalFilters = useMemo(
    () => ({
      year,
      month,
      bill_type: billType,
      location,
      customer_id: customerId !== ALL_CUSTOMERS ? customerId : undefined,
    }),
    [year, month, billType, location, customerId],
  )

  useEffect(() => {
    if (!unlocked) return

    let cancelled = false
    const run = async () => {
      if (!getPortalToken()) return
      setLoadingCustomers(true)
      try {
        const res = await fetchExportPortalCustomers({
          year,
          month,
          bill_type: billType,
          location,
        })
        if (cancelled) return
        setCustomers(res.data)
        setCustomerId((prev) => {
          if (prev === ALL_CUSTOMERS) return prev
          return res.data.some((c) => c.id === prev) ? prev : ALL_CUSTOMERS
        })
      } catch (err: any) {
        if (cancelled) return
        if (err?.response?.status === 401) {
          clearPortalSession()
          setUnlocked(false)
          setPinError(t('error_session_expired'))
          return
        }
        setCustomers([])
      } finally {
        if (!cancelled) setLoadingCustomers(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
    // `t` intentionally omitted — useTranslations() returns a new fn each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, year, month, billType, location])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setPinError('')
    setVerifying(true)
    try {
      await verifyExportPortalPin(pin)
      setUnlocked(true)
      setPin('')
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 429) setPinError(t('error_rate_limit'))
      else if (status === 400) setPinError(t('error_not_configured'))
      else setPinError(t('error_invalid_pin'))
    } finally {
      setVerifying(false)
    }
  }

  const handleLogout = () => {
    clearPortalSession()
    setUnlocked(false)
    setRows(null)
    setTotal(0)
    setCustomers([])
    setCustomerId(ALL_CUSTOMERS)
    setListError('')
    setExportError('')
    setExportOk('')
  }

  const handleLoad = async () => {
    setListError('')
    setExportOk('')
    setLoadingList(true)
    try {
      const res = await fetchExportPortalBills({ ...filters, page: 1, pageSize: 500 })
      setRows(res.data)
      setTotal(res.meta.total)
    } catch (err: any) {
      if (err?.response?.status === 401) {
        clearPortalSession()
        setUnlocked(false)
        setPinError(t('error_session_expired'))
        return
      }
      setListError(t('error_load'))
      setRows(null)
    } finally {
      setLoadingList(false)
    }
  }

  const handleExport = async (pdfMode: PdfMode) => {
    setExportError('')
    setExportOk('')
    setExporting(true)
    try {
      await exportPortalZip({ ...filters, template, pdf_mode: pdfMode })
      setExportOk(t('export_success'))
    } catch (err: any) {
      if (err?.response?.status === 401) {
        clearPortalSession()
        setUnlocked(false)
        setPinError(t('error_session_expired'))
        return
      }
      if (err?.response?.status === 404) {
        setExportError(t('export_empty'))
        return
      }
      setExportError(t('export_error'))
    } finally {
      setExporting(false)
    }
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-100 to-stone-200 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-stone-300/80 bg-white p-8 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-stone-900 text-white">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-stone-900">{t('title')}</h1>
              <p className="text-sm text-stone-500">{t('pin_subtitle')}</p>
            </div>
          </div>
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="export-pin">{t('pin_label')}</Label>
              <Input
                id="export-pin"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 32))}
                placeholder={t('pin_placeholder')}
                className="text-lg tracking-widest"
              />
            </div>
            {pinError ? <p className="text-sm text-red-600">{pinError}</p> : null}
            <Button type="submit" className="w-full" disabled={verifying || pin.length < 4}>
              {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('pin_submit')}
            </Button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gradient-to-b from-stone-100 to-stone-200">
      <header className="shrink-0 border-b border-stone-300/70 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4">
          <div>
            <h1 className="text-xl font-semibold text-stone-900">{t('title')}</h1>
            <p className="text-sm text-stone-500">{t('list_subtitle')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            {t('logout')}
          </Button>
        </div>
      </header>

      <main className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col gap-4 px-4 py-4">
        <section className="shrink-0 rounded-2xl border border-stone-300/80 bg-white p-4 shadow-sm sm:p-5">
          <h2 className="mb-4 text-base font-medium text-stone-800">{t('filters_title')}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label>{t('year')}</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('month')}</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_KEYS.map((key, i) => (
                    <SelectItem key={key} value={String(i + 1)}>
                      {t(key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('bill_type')}</Label>
              <Select
                value={billType}
                onValueChange={(v) => {
                  const next = v as 'buy' | 'sell'
                  setBillType(next)
                  setTemplate(defaultTemplateForBillType(next))
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buy">{t('bill_type_buy')}</SelectItem>
                  <SelectItem value="sell">{t('bill_type_sell')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('location')}</Label>
              <Select value={location} onValueChange={(v) => setLocation(v as 'chanthaburi' | 'chumphon')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chanthaburi">{t('location_chanthaburi')}</SelectItem>
                  <SelectItem value="chumphon">{t('location_chumphon')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{t('customer')}</Label>
              <Select value={customerId} onValueChange={setCustomerId} disabled={loadingCustomers}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingCustomers ? t('customer_loading') : t('customer_all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CUSTOMERS}>{t('customer_all')}</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name || c.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleLoad} disabled={loadingList}>
              {loadingList ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('load_list')}
            </Button>
          </div>
          {listError ? <p className="mt-3 text-sm text-red-600">{listError}</p> : null}
        </section>

        {rows ? (
          <section className="flex min-h-0 flex-1 flex-col rounded-2xl border border-stone-300/80 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-3 shrink-0 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-base font-medium text-stone-800">{t('results_title')}</h2>
                <p className="text-sm text-stone-500">{t('results_count', { count: total })}</p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('template')}</Label>
                  <Select value={template} onValueChange={(v) => setTemplate(v as BillExportTemplate)}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTemplates.map((tpl) => (
                        <SelectItem key={tpl} value={tpl}>
                          {t(TEMPLATE_LABEL_KEYS[tpl as keyof typeof TEMPLATE_LABEL_KEYS])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" disabled={exporting || total === 0} onClick={() => handleExport('csv')}>
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  {t('export_csv')}
                </Button>
                <Button variant="outline" disabled={exporting || total === 0} onClick={() => handleExport('report')}>
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  {t('export_pdf_report')}
                </Button>
                <Button disabled={exporting || total === 0} onClick={() => handleExport('receipts')}>
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  {t('export_pdf_receipts')}
                </Button>
              </div>
            </div>
            {exportError ? <p className="mb-2 shrink-0 text-sm text-red-600">{exportError}</p> : null}
            {exportOk ? <p className="mb-2 shrink-0 text-sm text-emerald-700">{exportOk}</p> : null}

            <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-stone-200">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="sticky top-0 z-10 bg-stone-50 text-stone-600 shadow-[0_1px_0_0_rgb(231,229,228)]">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('col_date')}</th>
                    <th className="px-3 py-2 font-medium">{t('col_bill_number')}</th>
                    <th className="px-3 py-2 font-medium">{t('col_customer')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('col_total')}</th>
                    <th className="px-3 py-2 font-medium">{t('col_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-stone-500">
                        {t('empty')}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="border-t border-stone-100">
                        <td className="px-3 py-2 whitespace-nowrap">{row.bill_date}</td>
                        <td className="px-3 py-2 font-medium">{row.bill_number}</td>
                        <td className="px-3 py-2">{row.customer_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatAmount(row.total_amount)}</td>
                        <td className="px-3 py-2">{row.payment_status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </main>
    </div>
  )
}
